import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import SomethingHappened from '@/components/SomethingHappened';
import { type LineageFlatEntry, useGetFishGroupLineage } from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import LineageGraph from '../lineage/LineageGraph';

import styles from '../FishGroupDetailModal.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
    onNavigateToFishGroup: (fishGroupId: number) => void;
}

type ViewMode = 'list' | 'graph';

const ROW_HEIGHT = 44;

export default function LineageTab(props: Props) {
    const { clientId, fishGroupId, onNavigateToFishGroup } = props;
    const { t } = useTranslation('fishGroups');
    const [viewMode, setViewMode] = useState<ViewMode>('graph');
    const { data: lineage, isLoading, error } = useGetFishGroupLineage(clientId, fishGroupId);

    if (isLoading) return <Spinner size="lg" />;
    if (error) return <SomethingHappened />;
    if (!lineage) return <EmptyState title={t('detail.lineage.empty')} appearance="quiet" />;

    return (
        <div>
            <ViewToggle value={viewMode} onChange={setViewMode} />
            {viewMode === 'list' ? (
                <LineageListView lineage={lineage} onNavigate={onNavigateToFishGroup} />
            ) : (
                <LineageGraph lineage={lineage} onNavigate={onNavigateToFishGroup} />
            )}
        </div>
    );
}

interface ViewToggleProps {
    value: ViewMode;
    onChange: (mode: ViewMode) => void;
}

function ViewToggle(props: ViewToggleProps) {
    const { value, onChange } = props;
    const { t } = useTranslation('fishGroups');
    return (
        <SegmentedControl<'graph' | 'list'>
            className={styles.viewToggle}
            aria-label={t('detail.lineage.viewToggle')}
            size="small"
            value={value}
            onChange={(view) => onChange(view)}
            options={[
                { value: 'graph', label: t('detail.lineage.viewGraph') },
                { value: 'list', label: t('detail.lineage.viewList') },
            ]}
        />
    );
}

interface LineageListViewProps {
    lineage: NonNullable<ReturnType<typeof useGetFishGroupLineage>['data']>;
    onNavigate: (fishGroupId: number) => void;
}

function LineageListView(props: LineageListViewProps) {
    const { lineage, onNavigate } = props;
    const { t } = useTranslation('fishGroups');

    return (
        <div>
            <section className={styles.lineageGroup}>
                <h4 className={styles.lineageHeading}>
                    {t('detail.lineage.ancestors', { count: lineage.flat.ancestors.length })}
                </h4>
                {lineage.flat.ancestors.length === 0 ? (
                    <p className={styles.emptyText}>{t('detail.lineage.noAncestors')}</p>
                ) : (
                    <LineageFlatTable entries={lineage.flat.ancestors} onNavigate={onNavigate} />
                )}
            </section>
            <section className={styles.lineageGroup}>
                <h4 className={styles.lineageHeading}>
                    {t('detail.lineage.descendants', { count: lineage.flat.descendants.length })}
                </h4>
                {lineage.flat.descendants.length === 0 ? (
                    <p className={styles.emptyText}>{t('detail.lineage.noDescendants')}</p>
                ) : (
                    <LineageFlatTable entries={lineage.flat.descendants} onNavigate={onNavigate} />
                )}
            </section>
        </div>
    );
}

interface LineageFlatTableProps {
    entries: LineageFlatEntry[];
    onNavigate: (fishGroupId: number) => void;
}

function LineageFlatTable(props: LineageFlatTableProps) {
    const { entries, onNavigate } = props;
    const { t } = useTranslation('fishGroups');

    const placeholder = <span className={styles.placeholder}>—</span>;

    const columns: DataTableColumnDef<LineageFlatEntry>[] = [
        {
            id: 'depth',
            header: t('detail.lineage.col.depth'),
            meta: { width: '0.5fr' },
            cell: ({ row }) => row.original.depth,
        },
        {
            id: 'name',
            header: t('detail.lineage.col.name'),
            meta: { width: '1.5fr' },
            cell: ({ row }) => (
                <Button
                    variant="link"
                    type="button"
                    onClick={() => onNavigate(row.original.fishGroup.id)}>
                    {row.original.fishGroup.name}
                </Button>
            ),
        },
        {
            id: 'status',
            header: t('detail.lineage.col.status'),
            meta: { width: '0.8fr' },
            cell: ({ row }) => row.original.fishGroup.status,
        },
        {
            id: 'started',
            header: t('detail.lineage.col.lifecycleStarted'),
            cell: ({ row }) =>
                row.original.fishGroup.lifecycleStartedAt
                    ? formatDateTimeWorded(row.original.fishGroup.lifecycleStartedAt, 'minute')
                    : placeholder,
        },
        {
            id: 'ended',
            header: t('detail.lineage.col.lifecycleEnded'),
            cell: ({ row }) =>
                row.original.fishGroup.lifecycleEndedAt
                    ? formatDateTimeWorded(row.original.fishGroup.lifecycleEndedAt, 'minute')
                    : placeholder,
        },
    ];

    return (
        <DataTable
            data={entries}
            columns={columns}
            getRowId={(entry) => String(entry.fishGroup.id)}
            layout="grid"
            enableSorting={false}
            rowHeight={ROW_HEIGHT}
            getRowProps={() => ({ disableHover: true })}
        />
    );
}
